'use client';

/**
 * LoadingText Component
 *
 * An animated text component that appends cycling dots (Loading. -> Loading.. -> Loading...).
 * Uses CSS keyframes via MUI's `keyframes` utility for the animation, and reserves space
 * with `width: Nch` on an inline-block span to prevent layout shift.
 *
 * Usage:
 * ```tsx
 * <LoadingText>Loading</LoadingText>
 * <LoadingText dots={4} speed={300}>Please wait</LoadingText>
 * ```
 */

import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { keyframes } from '@mui/material/styles';
import type { TypographyProps } from '@mui/material/Typography';
import Typography from '@mui/material/Typography';

// ============================================================================
// Types
// ============================================================================

export interface LoadingTextProps {
  /** Base text displayed before the animated dots. */
  children: string;

  /**
   * Maximum number of dots to cycle through.
   * @default 3
   */
  dots?: number;

  /**
   * Cycle speed per dot step in milliseconds.
   * @default 500
   */
  speed?: number;

  /**
   * Typography variant for the text.
   * @default 'body2'
   */
  variant?: TypographyProps['variant'];

  /** Additional styles applied to the root Typography element. */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Animation helpers
// ============================================================================

/**
 * Build a keyframes animation that cycles through '', '.', '..', '...' etc.
 * Each step is evenly spaced across 0-100%.
 */
function buildDotsKeyframes(maxDots: number) {
  const steps = maxDots + 1; // includes empty state at start and wrap-back
  const percentPerStep = 100 / steps;

  let framesCSS = '';
  for (let i = 0; i <= maxDots; i++) {
    const pct = Math.round(percentPerStep * i);
    const dots = '.'.repeat(i);
    framesCSS += `${pct}% { content: '${dots}'; }\n`;
  }
  framesCSS += `100% { content: ''; }\n`;

  return keyframes`${framesCSS}`;
}

// Pre-build the default (3-dot) animation for zero-cost reuse
const defaultDotsAnimation = buildDotsKeyframes(3);

// ============================================================================
// Component
// ============================================================================

// Cache keyframes per dots value to avoid recreating on every render
const keyframesCache = new Map<number, ReturnType<typeof buildDotsKeyframes>>();
keyframesCache.set(3, defaultDotsAnimation);

function getDotsKeyframes(maxDots: number) {
  const cached = keyframesCache.get(maxDots);
  if (cached) return cached;
  const kf = buildDotsKeyframes(maxDots);
  keyframesCache.set(maxDots, kf);
  return kf;
}

function LoadingText({ children, dots: rawDots = 3, speed = 500, variant = 'body2', sx }: LoadingTextProps) {
  const dots = Math.max(1, Math.min(10, Math.floor(rawDots)));
  const totalDuration = speed * (dots + 1);
  const animation = getDotsKeyframes(dots);

  return (
    <Typography
      variant={variant}
      sx={[
        { color: 'text.secondary', display: 'inline-flex', alignItems: 'baseline' },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {children}
      <Box
        component="span"
        data-testid="loading-dots"
        aria-hidden="true"
        sx={{
          display: 'inline-block',
          width: `${dots}ch`,
          textAlign: 'left',
          '&::after': {
            content: "''",
            animation: `${animation} ${totalDuration}ms steps(${dots + 1}, end) infinite`,
          },
          '@media (prefers-reduced-motion: reduce)': {
            '&::after': {
              content: `'${'.'.repeat(dots)}'`,
              animation: 'none',
            },
          },
        }}
      />
    </Typography>
  );
}

LoadingText.displayName = 'LoadingText';

export { LoadingText };
export default LoadingText;
